import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@lawline/ui/components/chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { useTranslations } from "use-intl";

import { formatBucket } from "@platform/features/usage/usage.utils";
import type { SpendPoint } from "../billing.types";

export function BillingSpendChart({
	points,
	currency,
}: {
	points: SpendPoint[];
	currency: string;
}) {
	const t = useTranslations("billing.spend");
	const config: ChartConfig = {
		amount: { label: t("cost"), color: "#000000" },
	};
	const money = (v: number) =>
		new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: currency.toUpperCase(),
		}).format(v);

	return (
		<ChartContainer config={config} className="aspect-auto h-[320px] w-full">
			<AreaChart data={points} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
				<CartesianGrid vertical={false} strokeDasharray="3 3" />
				<XAxis
					dataKey="t"
					tickLine={false}
					axisLine={false}
					tickMargin={8}
					minTickGap={24}
					tick={{ fontSize: 11 }}
					tickFormatter={(v) => formatBucket(String(v), "day")}
				/>
				<YAxis
					tickLine={false}
					axisLine={false}
					width={76}
					tick={{ fontSize: 11 }}
					tickFormatter={(v) => money(Number(v))}
				/>
				<ChartTooltip
					cursor={{ strokeDasharray: "3 3" }}
					content={
						<ChartTooltipContent
							labelFormatter={(_label, payload) => {
								const t = (payload?.[0]?.payload as SpendPoint | undefined)?.t;
								return t ? formatBucket(t, "day") : "";
							}}
							formatter={(value) => (
								<span className="text-foreground font-mono font-medium tabular-nums">
									{money(Number(value))}
								</span>
							)}
						/>
					}
				/>
				<Area
					dataKey="amount"
					type="monotone"
					stroke="#10b981"
					fill="#10b981"
					fillOpacity={0.2}
					strokeWidth={2}
				/>
			</AreaChart>
		</ChartContainer>
	);
}
